import { CLASS_OPEN } from "../tools/constants/classes";

type DisclosureOptionElement = HTMLElement & { dataset: { value: string } };

const SELECTOR_DISCLOSURE_OPTION = '[data-disclosure-option]';
const SELECTOR_DISCLOSURE_FORM = '[data-disclosure-form]';
const SELECTOR_DISCLOSURE_INPUT = '[data-disclosure-input]';
const SELECTOR_DISCLOSURE_TOGGLE = '[data-disclosure-toggle]';
const SELECTOR_DISCLOSURE_LIST = '[data-disclosure-list]';

export class Disclosure {
  private container: HTMLElement;
  private disclosureOptions: NodeListOf<DisclosureOptionElement>;
  private disclosureForm: HTMLFormElement | null;
  private disclosureInput: HTMLInputElement | null;
  private toggleButton: HTMLButtonElement | null;
  private list: HTMLElement | null;
  private clickHandler: (event: Event) => void;
  private outsideClickHandler: (event: MouseEvent) => void;

  constructor(container: HTMLElement) {
    this.container = container;
    this.disclosureOptions = this.container.querySelectorAll(SELECTOR_DISCLOSURE_OPTION);
    this.disclosureForm = this.container.closest<HTMLFormElement>(SELECTOR_DISCLOSURE_FORM);
    this.disclosureInput = this.container.querySelector<HTMLInputElement>(SELECTOR_DISCLOSURE_INPUT);
    this.toggleButton = this.container.querySelector(SELECTOR_DISCLOSURE_TOGGLE);
    this.list = this.container.querySelector(SELECTOR_DISCLOSURE_LIST);
    this.clickHandler = this._onOptionClick.bind(this);
    this.outsideClickHandler = this._onDocumentClick.bind(this);

    this._setupListeners();
  }

  private _setupListeners(): void {
    this.disclosureOptions.forEach((option) => {
      option.addEventListener('click', this.clickHandler);
    });

    if (this.toggleButton && this.list) {
      this.toggleButton.addEventListener('click', () => {
        const expanded = this.toggleButton?.getAttribute('aria-expanded') === 'true';
        this.toggleButton?.setAttribute('aria-expanded', (!expanded).toString());
        this.toggleButton?.classList.toggle(CLASS_OPEN);
        this.list?.classList.toggle(CLASS_OPEN);
      });
    }

    document.addEventListener('click', this.outsideClickHandler);
  }

  private _onDocumentClick(event: MouseEvent): void {
    if (!this.container.contains(event.target as Node)) {
      this._closeDisclosure();
    }
  }

  private _closeDisclosure(): void {
    if (this.list?.classList.contains(CLASS_OPEN)) {
      this.list.classList.remove(CLASS_OPEN);
      this.toggleButton?.setAttribute('aria-expanded', 'false');
    }
  }

  private _onOptionClick(event: Event): void {
    event.preventDefault();
    const target = event.currentTarget as DisclosureOptionElement;
    const value = target.dataset.value;

    if (this.disclosureInput && value) {
      this.disclosureInput.value = value;
      this.disclosureForm?.submit();
    }

    this._closeDisclosure();
  }

  public destroy(): void {
    this.disclosureOptions.forEach((option) => {
      option.removeEventListener('click', this.clickHandler);
    });

    document.removeEventListener('click', this.outsideClickHandler); 
  }
}
